import * as React from 'react';
import { Spin, Button, Tree } from 'antd';
import { resolve } from 'inversify-react';
import { isArrayLike } from 'mobx';

import { FormatService } from 'src/core/services/format.service';
import AuditService from 'src/app/general/audit/audit.service';
import HistoryViewTable from 'src/app/general/audit/parts/general-history.view';
import { TaskStateEditorView } from 'src/app/general/user-tasks/parts/task-state.editor';
import { UserEditorController } from 'src/app/general/users/parts/user-editor.controller';
import { AgentEditorController } from 'src/app/bookings/agents/parts/agent-editor.controller';
import { RegionEditorController } from 'src/app/general/regions/parts/region-editor.controller';
import { BrandEditorController } from 'src/app/general/brands/parts/brand-editor.controller';
import { PlaceEditorController } from 'src/app/general/places/parts/place-editor.controller';
import { TaxEditorController } from 'src/app/general/taxes/parts/tax-editor.controller';
import { AllotmentEditorController } from 'src/app/products/allotment/parts/allotment-editor.controller';
import { StatusTexts } from "src/app/shared/status-texts";

const TreeNode = Tree.TreeNode;

interface IProps {
	modelId: string;
	type?: 'general' | 'quote' | 'user-task';
	modelIdPreffix?: string;
}

interface IState {
	loading: boolean;
	pageIndex: number;
	pageSize: number;
	history: { items: any[], pageIndex: number, pageSize: number, total: number }
}

interface TreeProps {
	title: string;
	formatService: FormatService;
	obj: any;
}

interface TreeState {
	collapsed: boolean;
}

class DynamicObjectTree extends React.Component<TreeProps, TreeState>{

	constructor(props) {
		super(props);
		this.state = {
			collapsed: true,
		}
	}

	public render() {
		if (Object.keys(this.props.obj).length == 0) {
			return null;
		}

		return (
			<Tree showLine={true}>
				{this.getNodes(this.props.obj)}
			</Tree>
		);
	}

	private handleCollapse = (e) => {
		if (e) {
			e.preventDefault();
		}

		this.setState({ collapsed: !this.state.collapsed });
	}

	private viewByName = (name: string, value: any) => {
		const props: any = {
			value,
			mode: 'display',
			label: '',
			setBusy: e => { },
			onChanged: e => { }
		}

		switch (name) {
			case 'html':
			case 'body':
				return <div className='html-preview' dangerouslySetInnerHTML={{ __html: value }} />;
			case 'placeId':
				return <PlaceEditorController {...props} />;
			case 'agentId':
				return <AgentEditorController {...props} />;
			case 'taxId':
				return <TaxEditorController {...props} />;
			case 'allotmentId':
				return <AllotmentEditorController {...props} />;
			case 'modifiedBy':
				return <UserEditorController {...props} />;
		}

		return value;
	}

	private getNodes = (item) => {

		const result = [];

		for (let k in item) {
			const value = item[k];

			let inner = null;

			if (!value) {
				continue;
			}
			if (value instanceof Object) {
				const innerChild = this.getNodes(value);

				if (innerChild.length == 0) {
					continue;
				}

				inner = <TreeNode title={k} key={k}>{this.getNodes(value)}</TreeNode>;
			}
			else if (isArrayLike(value)) {

				if ((value as any[]).length == 0) {
					continue;
				}

				inner = (
					<TreeNode key={k} title={k}>
						{(value as any[]).map((v, index) => this.getNodes(v))}
					</TreeNode>
				);
			}
			else if (value instanceof Date) {
				inner = <TreeNode title={k + ': ' + this.props.formatService.toString('date-time', value, 'utc')} />;
			} else {
				inner = (
					<TreeNode title={k} key={k}>
						<TreeNode title={this.viewByName(k, value)} key={k} />
					</TreeNode>
				);
			}

			result.push(inner);
		}

		return result;
	}
}

export default class AuditController extends React.Component<IProps, IState>{

	@resolve
	auditService: AuditService

	@resolve
	formatService: FormatService;

	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			history: undefined,
			pageIndex: 0,
			pageSize: 10,
		}
	}

	public componentDidMount() {
		this.load(this.state.pageIndex, this.state.pageSize);
	}

	private load = async (pageIndex: number, pageSize: number) => {
		this.setState({ loading: true });
		try {
			const result = await this.auditService.getAuditHistory(this.props.modelId, this.props.modelIdPreffix, pageIndex, pageSize);
			this.setState({ loading: false, history: result.data });
		} catch (error) {
			this.setState({ loading: false });
		}
	}

	private userTaskPropertyRouter = (name: string, value: any) => {
		switch (name) {
			case 'State':
				return (
					<TaskStateEditorView
						mode='display'
						value={value}
						label=''
					/>
				);
			case 'AssignedToId':
				return (
					<UserEditorController
						label=""
						value={value}
						mode={'display'}
						setBusy={e => { }}
					/>
				);
		}

		return null;
	}

	private quotePropertyRouter = (name: string, value: any) => {
		switch (name) {
			case 'DynamicProperties':
				return (
					<DynamicObjectTree obj={value} title='DynamicProperty' formatService={this.formatService} />
				);
			case 'TravelRegionId':
				return (
					<RegionEditorController
						value={value}
						label=''
						mode='display'
						onChanged={e => { }}
						setBusy={e => { }}
					/>
				);
			case 'AgentId':
				return (
					<AgentEditorController
						value={value}
						label=''
						mode='display'
						onChanged={e => { }}
						setBusy={e => { }}
					/>
				);
			case "BrandId":
				return (
					<BrandEditorController
						label=''
						setBusy={e => { }}
						mode='display'
						value={value}
						onChanged={(newValue) => { }}
					/>
				);
			case 'Status':
				return this.getStatus(value);
		}

		return null;
	}

	private getStatus = (value) => {
		return StatusTexts.getQuoteEntryStatusFromString(value);
	}

	private onPageChange = (page: number) => {
		this.setState({ pageIndex: page });
		this.load(page, this.state.pageSize);
	}

	private getView() {

		switch (this.props.type) {
			case 'user-task':
				return <HistoryViewTable onPageChange={this.onPageChange} extraDiscrminator={this.userTaskPropertyRouter} data={this.state.history} />;
			case 'quote':
				return <HistoryViewTable onPageChange={this.onPageChange} extraDiscrminator={this.quotePropertyRouter} data={this.state.history} />;
		}

		return <HistoryViewTable onPageChange={this.onPageChange} data={this.state.history} />;
	}

	public render() {
		return (
			<Spin size={'large'} spinning={this.state.loading}>
				<Button
					onClick={() => this.load(this.state.pageIndex, this.state.pageSize)}
					style={{ margin: '10px', marginTop: '18px' }}
					icon="reload"
				/>
				{this.state.history && this.getView()}
			</Spin>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/audit/audit.controller.tsx